import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Alert, AlertColor, Snackbar, Stack } from '@mui/material';
import { DESLOCAMENTO_INFERIOR } from 'components/DeslocamentoInferior';

type Severity = AlertColor;

interface PushOptions {
  severity?: Severity;
  action?: ReactNode;
  autoHideDuration?: number | null;
  persist?: boolean;
}

interface SnackbarItem {
  id: number;
  message: ReactNode;
  severity: Severity;
  action: ReactNode | null;
  autoHideDuration: number | null;
}

interface PushHandle {
  id: number;
  dismiss: () => void;
  update: (next: Partial<SnackbarItem>) => void;
}

interface SnackbarApi {
  push: (message: ReactNode, opts?: PushOptions) => PushHandle;
  info: (m: ReactNode, o?: PushOptions) => PushHandle;
  success: (m: ReactNode, o?: PushOptions) => PushHandle;
  warning: (m: ReactNode, o?: PushOptions) => PushHandle;
  error: (m: ReactNode, o?: PushOptions) => PushHandle;
  dismiss: (id: number) => void;
}

const SnackbarContext = createContext<SnackbarApi | null>(null);

const DEFAULT_AUTO_HIDE = 5000;

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<SnackbarItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (message: ReactNode, opts: PushOptions = {}): PushHandle => {
      counter.current += 1;
      const id = counter.current;
      const item: SnackbarItem = {
        id,
        message,
        severity: opts.severity ?? 'info',
        action: opts.action ?? null,
        autoHideDuration: opts.persist ? null : (opts.autoHideDuration ?? DEFAULT_AUTO_HIDE)
      };
      setItems((prev) => [...prev, item]);
      return {
        id,
        dismiss: () => dismiss(id),
        update: (next: Partial<SnackbarItem>) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...next } : x)))
      };
    },
    [dismiss]
  );

  const api = useMemo<SnackbarApi>(
    () => ({
      push,
      info: (m, o) => push(m, { ...o, severity: 'info' }),
      success: (m, o) => push(m, { ...o, severity: 'success' }),
      warning: (m, o) => push(m, { ...o, severity: 'warning' }),
      error: (m, o) => push(m, { ...o, severity: 'error' }),
      dismiss
    }),
    [push, dismiss]
  );

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      {/* No rodapé, à direita; sobe quando uma peça fixa ocupa o canto (ver DeslocamentoInferior). */}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          bottom: `calc(16px + ${DESLOCAMENTO_INFERIOR})`,
          right: 16,
          zIndex: (t) => t.zIndex.snackbar,
          maxWidth: 420
        }}
      >
        {items.map((it) => (
          <Snackbar
            key={it.id}
            open
            autoHideDuration={it.autoHideDuration ?? undefined}
            onClose={(_, reason) => {
              if (reason !== 'clickaway') dismiss(it.id);
            }}
            sx={{ position: 'static', transform: 'none' }}
          >
            <Alert severity={it.severity} variant="filled" onClose={() => dismiss(it.id)} action={it.action} sx={{ width: '100%' }}>
              {it.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar deve ser usado dentro de SnackbarProvider.');
  return ctx;
}
